import { useMemo } from "react";
import { TrendingDown } from "lucide-react";
import { LAYERS, SURVEY_YEARS, profileBetween, rampCss } from "../../lib/sampleLake";

const toUnit = (ft, unit) => (unit === "m" ? ft * 0.3048 : ft);
const fmt = (ft, unit, d = 1) => `${toUnit(ft, unit).toFixed(d)} ${unit}`;

export const CompareCard = ({ stats }) => {
  const delta = stats.vegCoverage2025 - stats.vegCoverage;
  const pct = ((stats.vegAcres - stats.vegAcres2025) / stats.vegAcres) * 100;
  return (
    <div className="rounded-2xl border border-emerald-400/30 bg-emerald-400/5 p-5" data-testid="sample-map-compare-card">
      <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-emerald-300">
        Year-over-Year · {SURVEY_YEARS.before} → {SURVEY_YEARS.after}
      </div>
      <div className="mt-3 flex items-end gap-3">
        <div className="text-3xl font-extrabold font-mono text-white" data-testid="compare-coverage">
          {stats.vegCoverage.toFixed(0)}% <span className="text-slate-400">→</span> {stats.vegCoverage2025.toFixed(0)}%
        </div>
        <div className="inline-flex items-center gap-1 mb-1.5 px-2 py-0.5 rounded-md bg-emerald-500/20 text-emerald-200 font-mono text-xs font-bold" data-testid="compare-delta">
          <TrendingDown className="w-3.5 h-3.5" /> {delta.toFixed(0)} pts
        </div>
      </div>
      <div className="text-xs text-slate-400 mt-0.5">vegetated coverage of surface area</div>
      <div className="mt-4 grid grid-cols-2 gap-x-4 gap-y-3">
        <Stat label="Vegetated acres" value={`${stats.vegAcres.toFixed(0)} → ${stats.vegAcres2025.toFixed(0)}`} testId="compare-acres" />
        <Stat label="Mean biovolume" value={`${stats.meanBiovolume.toFixed(0)}% → ${stats.meanBiovolume2025.toFixed(0)}%`} testId="compare-biovolume" />
      </div>
      <p className="mt-4 text-sm text-slate-400 leading-relaxed">
        A repeat survey documents a <span className="text-emerald-200 font-semibold">{pct.toFixed(0)}% reduction</span> in vegetated
        area after targeted treatment in the shallow bays — proof for your board that the budget worked.
      </p>
    </div>
  );
};

export const Readout = ({ hover, unit, compare }) => (
  <div className="rounded-2xl border border-white/10 bg-[#061423]/70 p-5" data-testid="sample-map-readout">
    <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400">Cursor Reading</div>
    {hover && hover.water ? (
      <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-3">
        <div className="col-span-2">
          <div className="text-3xl font-extrabold font-mono text-cyan-300" data-testid="readout-depth">{fmt(hover.depth, unit)}</div>
          <div className="text-xs text-slate-400 mt-0.5">water depth</div>
        </div>
        {compare ? (
          <>
            <Stat label={`Veg. ${SURVEY_YEARS.before}`} value={`${hover.vegetation.toFixed(0)}%`} testId="readout-vegetation" />
            <Stat label={`Veg. ${SURVEY_YEARS.after}`} value={`${hover.vegetation2025.toFixed(0)}%`} testId="readout-vegetation-2025" />
          </>
        ) : (
          <>
            <Stat label="Vegetation" value={`${hover.vegetation.toFixed(0)}%`} testId="readout-vegetation" />
            <Stat label="Bottom" value={hover.hardness > 66 ? "Hard" : hover.hardness > 33 ? "Mixed" : "Soft"} testId="readout-hardness" />
          </>
        )}
        <div className="col-span-2 font-mono text-xs text-slate-400" data-testid="readout-coords">
          {hover.lat.toFixed(5)}°N, {Math.abs(hover.lon).toFixed(5)}°W
        </div>
      </div>
    ) : (
      <p className="mt-3 text-sm text-slate-400 leading-relaxed">
        {hover ? "Shoreline / land — move over the water." : "Hover or tap anywhere on the map to read live depth, vegetation and bottom type."}
      </p>
    )}
  </div>
);

const Stat = ({ label, value, testId }) => (
  <div data-testid={testId}>
    <div className="text-lg font-bold font-mono text-white">{value}</div>
    <div className="text-[11px] uppercase tracking-wider text-slate-400">{label}</div>
  </div>
);

export const Legend = ({ layer }) => {
  const l = LAYERS[layer];
  const min = l.stops[0][0], max = l.stops[l.stops.length - 1][0];
  return (
    <div className="rounded-2xl border border-white/10 bg-[#061423]/70 p-5" data-testid="sample-map-legend">
      <div className="flex items-center justify-between">
        <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400">Legend</div>
        <div className="text-xs text-slate-400">{l.label} · {l.unit}</div>
      </div>
      <div className="mt-3 h-3 rounded-full" style={{ background: rampCss(layer) }} />
      <div className="mt-1.5 flex justify-between font-mono text-[11px] text-slate-400">
        <span>{layer === "hardness" ? "soft" : min}</span>
        <span>{layer === "hardness" ? "hard" : `${max}${layer === "depth" ? " ft" : "%"}`}</span>
      </div>
      <div className="mt-3 flex items-center gap-2 text-[11px] text-slate-400">
        <span className="inline-block w-6 h-px bg-slate-950" /> 5 ft depth contours
      </div>
    </div>
  );
};

export const SurveyStats = ({ stats, unit }) => {
  const items = [
    { label: "Max depth", value: fmt(stats.maxDepth, unit) },
    { label: "Mean depth", value: fmt(stats.meanDepth, unit) },
    { label: "Surface area", value: `${stats.acres.toFixed(0)} ac` },
    { label: "Volume", value: `${Math.round(stats.volumeAcreFt).toLocaleString()} ac-ft` },
    { label: "Veg. coverage", value: `${stats.vegCoverage.toFixed(0)}%` },
    { label: "Sonar points", value: stats.points.toLocaleString() },
  ];
  return (
    <div className="rounded-2xl border border-white/10 bg-[#061423]/70 p-5" data-testid="sample-map-stats">
      <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400">Survey Summary</div>
      <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-3">
        {items.map((s) => (
          <Stat key={s.label} label={s.label} value={s.value} testId={`stat-${s.label.toLowerCase().replace(/[^a-z]+/g, "-")}`} />
        ))}
      </div>
    </div>
  );
};

export const ProfileChart = ({ points, unit }) => {
  const data = useMemo(() => (points.length === 2 ? profileBetween(points[0], points[1]) : null), [points]);
  if (!data) return null;
  const W = 320, H = 110, pad = 6;
  const maxD = Math.max(...data.map((p) => p.depth), 1);
  const total = data[data.length - 1].dist;
  const path = data.map((p, i) => `${i ? "L" : "M"}${pad + (p.dist / total) * (W - pad * 2)},${pad + (p.depth / maxD) * (H - pad * 2)}`).join(" ");
  const distLabel = unit === "m" ? `${(total * 0.3048).toFixed(0)} m` : total > 1500 ? `${(total / 5280).toFixed(2)} mi` : `${total.toFixed(0)} ft`;
  return (
    <div className="rounded-2xl border border-amber-400/30 bg-amber-400/5 p-5" data-testid="sample-map-profile">
      <div className="flex items-center justify-between">
        <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-amber-300">Depth Profile A → B</div>
        <div className="font-mono text-sm font-bold text-white" data-testid="profile-distance">{distLabel}</div>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} className="mt-3 w-full h-28" aria-label="Depth profile between points A and B">
        <defs>
          <linearGradient id="profileFill" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0" stopColor="#38BDF8" stopOpacity="0.15" />
            <stop offset="1" stopColor="#0B1033" stopOpacity="0.9" />
          </linearGradient>
        </defs>
        <line x1={pad} x2={W - pad} y1={pad} y2={pad} stroke="#67E8F9" strokeOpacity="0.6" strokeDasharray="3 3" />
        <path d={`${path} L${W - pad},${H} L${pad},${H} Z`} fill="url(#profileFill)" />
        <path d={path} fill="none" stroke="#FDE68A" strokeWidth="2" />
      </svg>
      <div className="mt-1 flex justify-between font-mono text-[11px] text-slate-400">
        <span>A · {fmt(data[0].depth, unit)}</span>
        <span>deepest {fmt(maxD, unit)}</span>
        <span>B · {fmt(data[data.length - 1].depth, unit)}</span>
      </div>
    </div>
  );
};
